// English copy for the public "How preview-first uploads work" page (/preview-first-uploads).
// English is the source of truth: its shape is the PreviewFirstCopy type that copy.es.ts and
// copy.pt.ts must match. Every claim here is backed by the web uploader or the server:
//  - previews: 1200px copies made in the browser, uploaded first (UploaderExplorer, PREVIEW_COPY_RESIZE);
//  - go live: "Publish now" / "Publish the gallery as soon as the previews are up" (GoLiveStrip,
//    PreviewFirstOption.GoLiveChoice), owners/administrators only;
//  - sold first: ordered photos' originals are sent ahead of the rest (useUploadManager + soldPriority);
//    the uploader names the ones a buyer is waiting for (BuyerWaitingBanner);
//  - visitors in the last 30 min for every uploader once published; orders for owners/administrators
//    (GoLiveStrip);
//  - delivery: a copy is never delivered as a purchase; held order emails retry until the original lands
//    (photohawk-serverless docs/preview-first-uploads.md);
//  - closed early: "N photos are still waiting… add the folder again" (UploaderExplorer);
//  - FTP and Photohawk Studio do not upload previews first (only the web uploader asks for them);
//  - plans: every plan (owner, 19 Oct 2026; entitlement previewFirst), and photos already up as previews always
//    finish; off by default: the upload summary shows the time each way and the person chooses "Previews first,
//    originals later" (UploadChoice), remembered per browser;
//  - free downloads: never delivered as the preview; the original jumps the queue, the buyer's download page
//    shows it uploading and swaps to the download, a held emailed download goes when it lands; counted once.
//    (photohawk-serverless docs/preview-first-uploads.md "Free downloads", photohawk-app orderOriginals).
// Never "photographer" or "organiser": the reader may be anyone uploading (copy.test.ts checks).

const en = {
  seo: {
    title: "Preview-first uploads, on every plan | Photohawk",
    description:
      "New in the Photohawk web uploader, on every plan: a small preview of each photo goes up first, so your gallery can go live, sell and be shared within minutes while the originals follow in the background.",
  },
  hero: {
    badge: "New",
    eyebrow: "In the web uploader · Every plan",
    title: "Your gallery is live in minutes,",
    titleHighlight: "not hours",
    subtitle:
      "The web uploader now sends a small preview of every photo first. Your gallery can go live on those previews, so buyers can find, buy and share their photos while the originals keep uploading in the background.",
    ctaPrimary: "Open the uploader",
    ctaSecondary: "See how it works",
    /** Back to the web uploader launch page (/web-uploader). */
    uploaderLink: "See everything that's new in the web uploader",
  },
  steps: {
    heading: "How it works",
    subtitle: "Preview-first is in the web uploader, on every plan. When you add photos, it shows how long the upload takes each way, and you choose “Previews first, originals later”.",
    /** "{n}" is the step number. */
    stepLabel: "Step {n}",
    items: [
      {
        id: "add",
        title: "Add your photos",
        body: "Drop in photos or a whole folder, just as you do today. Your browser makes a small preview of each one, right on your computer.",
      },
      {
        id: "previews",
        title: "Previews go up first",
        body: "The previews are small, so they upload quickly, even on a slow connection at the venue.",
      },
      {
        id: "live",
        title: "Your gallery goes live",
        body: "Publish as soon as the previews are up, or tick “Publish the gallery as soon as the previews are up” and it happens on its own. Buyers can browse, search, buy and share straight away.",
      },
      {
        id: "full",
        title: "The originals follow",
        body: "The originals carry on uploading in the background, into the same photos. Downloads and prints always use the originals.",
      },
    ],
    timeline: {
      label: "An example upload, from start to finish",
      previews: "Previews",
      previewsTime: "minutes",
      live: "Gallery live",
      full: "Originals",
      fullTime: "in the background",
    },
  },
  buyers: {
    heading: "What buyers see",
    subtitle: "Nothing new to learn. Buyers get the gallery they know, just sooner.",
    items: [
      {
        id: "protected",
        title: "Watermarked and protected, as usual",
        body: "Buyers browse the same watermarked previews as always, and preview protection works just the same if your gallery uses it.",
      },
      {
        id: "search",
        title: "They can find themselves straight away",
        body: "Selfie and bib search work on the previews, so people find their photos as soon as the gallery is live.",
      },
      {
        id: "delivery",
        title: "Originals, delivered automatically",
        body: "A purchase or a free download is never delivered as the preview. If someone buys or downloads a photo before its original has arrived, it moves to the front of the queue, and they get it as soon as it's ready.",
      },
    ],
  },
  strip: {
    heading: "Keep an eye on it while you upload",
    body: "While previews and originals upload, a strip at the top of the uploader shows where your gallery stands: whether it's live yet, how many originals are still to go, and when a bought photo has moved to the front. Once the gallery is live, it also shows how many people visited it in the last 30 minutes, and owners and administrators of the workspace see new orders since the upload started.",
    exampleLabel: "Example",
    live: "Live on previews",
    orders: "3 orders since you started",
    visitors: "42 visitors in the last 30 min",
    left: "1,250 originals to go",
    jumped: "2 ordered photos' originals moved to the front",
  },
  faq: {
    heading: "Questions",
    items: [
      {
        q: "Do I need to keep the uploader open?",
        a: "It's best to, until the originals finish; the strip shows how many are left. If you close it early, nothing is lost. Your gallery stays live on the previews, and the next time you open that gallery in the uploader it tells you how many photos are still waiting. Add the same folder again (even later, from home) and only the missing originals are sent.",
      },
      {
        q: "What if someone buys a photo before its original arrives?",
        a: "They can still buy it. While the uploader is running, that photo's original moves to the front of the queue. The buyer's order email waits for the file and goes out within minutes of it landing, so they never receive the preview by mistake. If the uploader was closed, the order waits until you add the folder again. The uploader tells you which photos a buyer is waiting for, and those go first.",
      },
      {
        q: "Does it work for free-download galleries?",
        a: "Yes. People can view, search and download as soon as the previews are up, and a download is never the preview. If someone downloads a photo before its original has arrived, it moves to the front of the queue: their download shows that the original is still uploading and becomes a working download the moment it lands, and an emailed download goes out then too. Each download counts once.",
      },
      {
        q: "Does selfie and bib search work on the previews?",
        a: "Yes. Faces and bib numbers are found on the previews, so search works as soon as they're up. While the uploader is open, it also sends sharper close-ups taken from your originals to help search.",
      },
      {
        q: "Which plans include it?",
        a: "Every plan, Lite included. If your workspace used preview-first before, those photos still finish as normal: add the same folder again and their originals are sent, and their orders and downloads still arrive.",
      },
      {
        q: "Does it cost anything extra?",
        a: "No. It's included on every plan, and each photo counts once: the preview and its original are the same photo. The only difference is that your connection sends a little more data in total (about 5% more).",
      },
      {
        q: "Does it work with FTP and Photohawk Studio?",
        a: "Not at the moment. Preview-first is part of the web uploader. Photos sent by FTP or from Photohawk Studio upload as originals in one go, as before.",
      },
      {
        q: "Is it on by default?",
        a: "No. Each time you add photos, the uploader shows how long they'll take with and without previews first, and sending the originals straight away is selected. Choose “Previews first, originals later” when you want the gallery live sooner; the uploader remembers your choice for next time. A smaller upload resolution (Large or Web) uploads resized files only, so previews first doesn't apply then.",
      },
    ],
  },
  cta: {
    heading: "Try it on your next upload",
    body: "Preview-first is in the web uploader, on every plan. Add your photos, choose previews first, and your gallery can be live in minutes.",
    button: "Open the uploader",
  },
};

export type PreviewFirstCopy = typeof en;
export default en;
